import React, { useState, useEffect } from 'react';
import { api, fetchData } from '../services/api';
import { PATH_SEGMENTS } from '../config/paths';
import KPIDashboard from './KPIDashboard';
import { useKPILoading } from '../hooks/useKPILoading';
import '../styles/components/ScriptAdherence.css';

interface Step {
  name: string;
  status: 'Compliant' | 'Missed' | 'Out of order';
  expected_steps: string;
  notes: string;
  timestamp: string;
}

interface Section {
  name: string;
  steps: {
    [key: string]: Step;
  };
}

interface AdherenceData {
  sections: {
    [key: string]: Section;
  };
}

interface ScriptAdherenceProps {
  interactionId: string | number;
  transcriptId: string | number;
  onTimestampClick?: (timestamp: string) => void;
}

const ScriptAdherence: React.FC<ScriptAdherenceProps> = ({ interactionId, transcriptId, onTimestampClick }) => {
  const [adherenceData, setAdherenceData] = useState<AdherenceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // KPI loading using custom hook
  const {
    kpiDataMap,
    kpiLoading,
    kpiError,
    getKpiData
  } = useKPILoading({
    interactionIds: [interactionId],
    kpiNames: ['script_adherence', 'script_violations']
  });

  const kpiAdherence = getKpiData(interactionId, 'script_adherence');
  const kpiScriptViolations = getKpiData(interactionId, 'script_violations');
  
  // Log KPI loading status for debugging
  useEffect(() => {
    if (kpiError) {
      console.warn('KPI loading failed in ScriptAdherence:', kpiError);
    }
    if (kpiLoading) {
      console.log('KPI data loading in ScriptAdherence...');
    }
  }, [kpiError, kpiLoading]);

  useEffect(() => {
    const fetchAdherenceData = async () => {
      try {
        const data = await fetchData<AdherenceData>(api(PATH_SEGMENTS.ADHERENCE, transcriptId));

        // Validate the data structure
        if (!data) {
          throw new Error('No adherence data available');
        }

        // Check if data is a string (might be JSON string)
        if (typeof data === 'string') {
          try {
            const parsedData = JSON.parse(data);
            if (parsedData && parsedData.sections) {
              setAdherenceData(parsedData);
              setLoading(false);
              return;
            }
          } catch (parseError) {
            throw new Error('Invalid adherence data format: data is not valid JSON');
          }
        }

        // If data is already an object
        if (typeof data === 'object' && data !== null) {
          if (!data.sections) {
            throw new Error('Invalid adherence data format: sections property is missing');
          }

          // Validate each section
          Object.entries(data.sections).forEach(([sectionId, section]) => {
            if (!section || typeof section !== 'object') {
              throw new Error(`Invalid section format for section ${sectionId}`);
            }
            if (!section.name || typeof section.name !== 'string') {
              throw new Error(`Invalid section name for section ${sectionId}`);
            }
            if (!section.steps || typeof section.steps !== 'object') {
              throw new Error(`Invalid steps format for section ${sectionId}`);
            }
          });

          setAdherenceData(data);
          setLoading(false);
          return;
        }

        throw new Error('Invalid adherence data format: unexpected data type');
      } catch (err) {
        console.error('Error fetching adherence data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load adherence data');
        setLoading(false);
      }
    };

    fetchAdherenceData();
  }, [transcriptId]);

  // KPI loading is now handled by the useKPILoading hook
  // The hook automatically loads KPI data when the component mounts

  const formatName = (name: string): string => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getStatusIcon = (status: string): string => {
    const normalizedStatus = status.toLowerCase();
    switch (normalizedStatus) {
      case 'compliant':
        return '✅';
      case 'missed':
        return '❌';
      case 'out of order':
      case 'out_of_order':
        return '⚠️';
      default:
        return '⚠️';
    }
  };

  const handleTimestampClick = (timestamp: string) => {
    if (timestamp !== 'N/A' && onTimestampClick) {
      onTimestampClick(timestamp);
    }
  };

  if (loading) {
    return <div className="script-adherence-loading">Loading adherence data...</div>;
  }

  if (error) {
    return <div className="script-adherence-error">{error}</div>;
  }

  if (!adherenceData || !adherenceData.sections) {
    return <div className="script-adherence-error">No adherence data available</div>;
  }

  // Get sections as an array and sort them by section ID
  const sections = Object.entries(adherenceData.sections)
    .sort(([idA], [idB]) => parseInt(idA) - parseInt(idB));

  // Check if we have any valid KPI data to show
  const hasValidKpiData = kpiAdherence || kpiScriptViolations;
  
  return (
    <div className="script-adherence">
      {/* KPI Dashboard - show when loading or when we have valid data */}
      {(kpiLoading || hasValidKpiData) && (
        <div className="kpi-dashboard-container">
          <KPIDashboard
            rows={[
              {
                id: 'adherence-score-row',
                type: 'score',
                kpis: [kpiAdherence, kpiScriptViolations].filter(Boolean)
              }
            ]}
            loading={kpiLoading}
            error={null} // Don't show global error, let individual widgets handle errors
          />
        </div>
      )}
      
      <div className="adherence-table-container">
        <table className="adherence-table">
          <thead>
            <tr>
              <th>Section</th>
              <th>Step</th>
              <th>Status</th>
              <th>Timestamp</th>
              <th>Expected</th>
            </tr>
          </thead>
          <tbody>
            {sections.map(([sectionId, section]) => (
              Object.entries(section.steps)
                .sort(([idA], [idB]) => parseInt(idA) - parseInt(idB))
                .map(([stepId, step]) => (
                  <tr key={`${sectionId}-${stepId}`} className={`status-${step.status.toLowerCase().replace(' ', '-')}`}>
                    <td>{formatName(section.name)}</td>
                    <td>{formatName(step.name)}</td>
                    <td>
                      <span className="status-cell">
                        {getStatusIcon(step.status)}
                        <span className="status-text">{step.status}</span>
                      </span>
                    </td>
                    <td>
                      <span 
                        className={`timestamp ${step.timestamp !== 'N/A' ? 'clickable' : ''}`}
                        onClick={() => handleTimestampClick(step.timestamp)}
                      >
                        {step.timestamp}
                      </span>
                    </td>
                    <td>
                      {step.status !== 'Compliant' && (
                        <div className="expected-steps">
                          <div className="expected-label">Expected:</div>
                          <div className="expected-content">{step.expected_steps}</div>
                          {step.notes && (
                            <div className="step-notes">{step.notes}</div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScriptAdherence; 